import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { breakpoints } from '@coursera/cds-core';

type WidthData = {
  spacingBetweenItems: number;
  itemPadding: number;
  itemBorder: number;
  buttonWidth: number;
  iconWidth: number;
  spacingBetweenIconAndText: number;
};

type Props = {
  containerRef: RefObject<HTMLDivElement>;
  items: string[];
  pinnedItem: string;
  widthData: WidthData;
  isExpanded?: boolean;
};

type GetItemWidthProps = {
  item: string;
  widthData: WidthData;
  hasIcon: boolean;
};

const ROW_CONFIG = [
  { minWidth: breakpoints.values.md, row: 1 },
  { minWidth: breakpoints.values.sm, row: 2 },
  { minWidth: 0, row: 4 },
] as const;

const WIDTH_BUFFER = [
  { minWidth: breakpoints.values.sm, buffer: 10 },
  { minWidth: 0, buffer: 5 },
] as const;

const DEBOUNCE_DELAY = 100;
const STEP_SIZE = 20;

function getTextWidth(text: string, fontSize = '0.875rem', fontWeight = '400'): number {
  const canvas = document.createElement('canvas');
  const canvasContext = canvas.getContext('2d');
  if (!canvasContext) return 0;

  canvasContext.font = `${fontWeight} ${fontSize} "Source Sans Pro", Arial, sans-serif`;
  canvasContext.letterSpacing = '0.01em';

  return Math.floor(canvasContext.measureText(text).width);
}

function getItemWidth({ item, widthData, hasIcon }: GetItemWidthProps): number {
  const { itemPadding, spacingBetweenItems, iconWidth, spacingBetweenIconAndText, itemBorder } = widthData;

  return (
    getTextWidth(item) +
    itemPadding +
    spacingBetweenItems +
    (hasIcon ? iconWidth + spacingBetweenIconAndText : 0) +
    itemBorder
  );
}

function useDebouncedDivWidth({
  ref,
  step = STEP_SIZE,
  debounceDelay = DEBOUNCE_DELAY,
  isExpanded = false,
}: {
  ref: RefObject<HTMLDivElement>;
  step?: number;
  debounceDelay?: number;
  isExpanded?: boolean;
}) {
  const [rowNum, setRowNum] = useState(1);
  const [width, setWidth] = useState<number | null>(null);
  const lastWidthRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!ref.current) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      const windowWidth = window.innerWidth;
      // @ts-expect-error TS2339 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const { buffer } =
        WIDTH_BUFFER.find((config) => windowWidth >= config.minWidth) ?? WIDTH_BUFFER[WIDTH_BUFFER.length - 1];
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const newWidth = entry.contentRect.width - buffer;

      // @ts-expect-error TS2339 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      const { row: baseRowNum } =
        ROW_CONFIG.find((config) => windowWidth >= config.minWidth) ?? ROW_CONFIG[ROW_CONFIG.length - 1];
      const newRowNum = isExpanded ? baseRowNum * 2 : baseRowNum;

      const widthChanged = Math.abs(newWidth - (lastWidthRef.current ?? 0)) >= step;
      const rowNumChanged = rowNum !== newRowNum;

      if (widthChanged || rowNumChanged) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          lastWidthRef.current = newWidth;
          setWidth(newWidth);
          setRowNum(newRowNum);
        }, debounceDelay);
      }
    });

    observer.observe(ref.current);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      observer.disconnect();
    };
  }, [step, debounceDelay, ref, isExpanded, rowNum]);

  return { width, rowNum };
}

type Layout = {
  numberOfItemsPerRow: number[];
  shouldUseDefaultLayout: boolean;
  hasShownAllItems: boolean;
};

export type UseClientSideNumberOfItemsPerRowResult = Layout & {
  isHydrated: boolean;
};

/**
 * Hook to calculate the number of items per row based on container width and screen size.
 * Uses CDS breakpoints for responsive layout:
 * - xs: 4 items per row
 * - sm: 2 items per row
 * - md: 1 item per row
 *
 * The layout logic should fall back to default rendering (i.e., wrapping all items) when there aren't enough items
 * to fill the computed number of rows.
 *
 * @param containerRef - Reference to the container element
 * @param items - Array of item text strings
 * @param pinnedItem - Text of the pinned item
 * @param widthData - Object containing spacing and sizing measurements:
 *   - spacingBetweenItems: Gap between items
 *   - itemPadding: Internal left+right padding of each item
 *   - itemBorder: Border left+right width of each item
 *   - buttonWidth: Width of the action button
 *   - iconWidth: Width of the item icon
 *   - spacingBetweenIconAndText: Gap between icon and text
 * @param isExpanded - Whether the container is in expanded state
 * @returns An object with:
 *   - numberOfItemsPerRow: Number of items to display in each row
 *   - shouldUseDefaultLayout: Whether the fallback wrapping layout should be used
 *   - hasShownAllItems: Whether all items have been shown
 *   - isHydrated: Whether layout has finished calculating on the client */
export default function useClientSideNumberOfItemsPerRow({
  containerRef,
  items,
  pinnedItem,
  widthData,
  widthData: { buttonWidth },
  isExpanded = false,
}: Props): UseClientSideNumberOfItemsPerRowResult {
  const [numberOfItemsPerRow, setNumberOfItemsPerRow] = useState<number[]>([]);
  const [shouldUseDefaultLayout, setShouldUseDefaultLayout] = useState(false);
  const [hasShownAllItems, setHasShownAllItems] = useState(false);
  const lastLayoutRef = useRef<Layout | null>(null);

  const pinnedItemWidth = useMemo(
    () => getItemWidth({ item: pinnedItem, widthData, hasIcon: false }),
    [pinnedItem, widthData]
  );

  const { rowNum, width } = useDebouncedDivWidth({
    ref: containerRef,
    step: STEP_SIZE,
    debounceDelay: DEBOUNCE_DELAY,
    isExpanded,
  });

  const itemWidths = useMemo(
    () => items.map((item) => getItemWidth({ item, widthData, hasIcon: true })),
    [items, widthData]
  );

  const calculateCustomLayout = useCallback<
    () => {
      numberOfItemsPerRow: number[];
      shouldUseDefaultLayout: boolean;
      hasShownAllItems: boolean;
    }
  >(() => {
    if (width === null || items.length === 0)
      return { numberOfItemsPerRow: [], shouldUseDefaultLayout: true, hasShownAllItems: false };

    let previousLastRowIndex = 0;
    const currentNumberOfItemsPerRow: number[] = [];

    for (let i = 0; i < rowNum; i += 1) {
      const isLastRow = i === rowNum - 1;
      const targetWidth = isLastRow ? width - pinnedItemWidth - buttonWidth : width;

      let itemsInRow = 0;
      let currentRowWidth = 0;

      // Try to fit as many items as possible in this row
      for (let j = previousLastRowIndex; j < items.length; j += 1) {
        const itemWidth = itemWidths[j];
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        if (currentRowWidth + itemWidth <= targetWidth) {
          // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          currentRowWidth += itemWidth;
          itemsInRow += 1;
        } else {
          break;
        }
      }

      // If we couldn't fit any items, force at least one item
      if (itemsInRow === 0 && previousLastRowIndex < items.length && i !== rowNum - 1) {
        itemsInRow = 1;
      }

      currentNumberOfItemsPerRow.push(itemsInRow);
      previousLastRowIndex += itemsInRow;

      // If we've reached the end of items, break
      if (previousLastRowIndex === items.length) {
        break;
      }
    }

    const allItemsShown = previousLastRowIndex === items.length;

    // Check if we've placed the last item (`items.length - 1`) and still have unused rows.
    // This indicates there aren't enough items to fill all rows,
    // so we can fall back to default layout (i.e., allow items to wrap naturally).
    const haveNotEnoughItemsForAllRows = allItemsShown && currentNumberOfItemsPerRow.length < rowNum;

    return {
      numberOfItemsPerRow: currentNumberOfItemsPerRow,
      shouldUseDefaultLayout: haveNotEnoughItemsForAllRows,
      hasShownAllItems: allItemsShown,
    };
  }, [width, rowNum, pinnedItemWidth, buttonWidth, items.length, itemWidths]);

  useEffect(() => {
    const layout = calculateCustomLayout();
    const hasChanged =
      !lastLayoutRef.current ||
      lastLayoutRef.current.shouldUseDefaultLayout !== layout.shouldUseDefaultLayout ||
      lastLayoutRef.current.hasShownAllItems !== layout.hasShownAllItems ||
      lastLayoutRef.current.numberOfItemsPerRow.length !== layout.numberOfItemsPerRow.length ||
      lastLayoutRef.current.numberOfItemsPerRow.some((val, i) => val !== layout.numberOfItemsPerRow[i]);

    if (hasChanged) {
      lastLayoutRef.current = layout;
      setNumberOfItemsPerRow(layout.numberOfItemsPerRow);
      setShouldUseDefaultLayout(layout.shouldUseDefaultLayout);
      setHasShownAllItems(layout.hasShownAllItems);
    }
  }, [calculateCustomLayout]);

  return {
    numberOfItemsPerRow,
    shouldUseDefaultLayout,
    hasShownAllItems,
    isHydrated: width !== null,
  };
}
